import { enter, leave } from 'el-transition'
import { Controller } from '@hotwired/stimulus'
import { get } from '@rails/request.js'

export default class extends Controller {
  static outlets = [
    'clients--album-upload-form',
    'clients--picture-upload-form',
  ]

  static targets = [
    'albumTemplatePlaceholder',
    'dragZone',
    'fileList',
    'fileTemplate',
    'fileUploadContainer',
    'fileUploadPlaceholder',
    'hiddenFileInput',
    'submit',
    'submittingPlaceholder',
    'successPlaceholder',
  ]

  static values = {
    folderId: String,
    historical: { default: false, type: Boolean },
    state: { default: 'waitingForTemplate', type: String },
    templateUrl: String,
  }

  connect() {
    this.dragOverStyle = []

    if (this.hasDragZoneTarget) {
      const { dragOverStyle } = this.dragZoneTarget.dataset
      if (dragOverStyle) this.dragOverStyle = dragOverStyle.split(' ')
    }
  }

  applyDragOverStyles() {
    this.dragZoneTarget.classList.add(...this.dragOverStyle)
  }

  clientsPictureUploadFormOutletConnected() {
    this.stateValue = 'active'
  }

  clientsPictureUploadFormOutletDisconnected() {
    const validStates = ['active', 'submittingAlbum', 'submittingPictures']

    if (validStates.includes(this.stateValue)) {
      if (!this.hasClientsPictureUploadFormOutlet &&
        this.stateValue === 'active') {
        this.stateValue = 'waitingForFileUpload'
      } else {
        this.#checkFormsForAllSuccess()
      }
    }
  }

  folderIdValueChanged() {
    this.fileListTarget.innerHTML = ''
    this.stateValue = 'waitingForTemplate'

    if (this.folderIdValue) this.#fetchTemplates()
  }

  handleDragOver(event) {
    event.preventDefault()
  }

  handleDrop(event) {
    event.preventDefault()
    this.removeDragOverStyles()

    if (event.dataTransfer.items) {
      [...event.dataTransfer.items].forEach((item) => {
        if (item.kind === 'file') {
          this.#addFileForm(item.getAsFile())
        }
      })
    } else {
      [...event.dataTransfer.files].forEach((file) => this.#addFileForm(file))
    }
  }

  handleFileInput(event) {
    const { files } = event.target

    if (files.length) {
      [...files].forEach((file) => this.#addFileForm(file))
    }
  }

  handleFolderChange({ detail: { folderId } }) {
    if (this.historicalValue) {
      this.#show()
    } else {
      this.folderIdValue = folderId
    }
  }

  handleModalHide() {
    if (this.stateValue === 'success') {
      this.folderIdValue = ''
    }
  }

  openFileInput() {
    this.hiddenFileInputTarget.click()
  }

  removeDragOverStyles() {
    this.dragZoneTarget.classList.remove(...this.dragOverStyle)
  }

  async submitForm({ detail: { submitPromise } }) {
    await submitPromise

    this.#checkFormsForAllSuccess()
  }

  async submitAll() {
    if (this.stateValue === 'active') {
      this.stateValue = 'submittingAlbum'
    }
  }

  stateValueChanged() {
    switch (this.stateValue) {
      case 'active':
        this.submitTarget.innerHTML = this.submitTarget.dataset.activeText
        this.submitTarget.disabled = false
        this.#show(
          this.albumTemplatePlaceholderTarget,
          this.fileListTarget,
          this.fileUploadContainerTarget,
          this.submitTarget,
        )
        this.dispatch('uploadStateChange', { detail: { disableInput: false } })
        break
      case 'submittingAlbum':
        this.submitTarget.innerHTML = this.submitTarget.dataset.submittingText
        this.submitTarget.disabled = true
        this.#show(
          this.albumTemplatePlaceholderTarget,
          this.fileListTarget,
          this.submittingPlaceholderTarget,
          this.submitTarget,
        )
        this.dispatch('uploadStateChange', { detail: { disableInput: true } })
        this.#submitAlbumForm()
        break
      case 'submittingPictures':
        this.#show(
          this.albumTemplatePlaceholderTarget,
          this.fileListTarget,
          this.submittingPlaceholderTarget,
          this.submitTarget,
        )
        this.dispatch('uploadStateChange', { detail: { disableInput: true } })
        this.#submitPictures()
        break
      case 'success':
        this.#show(
          this.albumTemplatePlaceholderTarget,
          this.fileListTarget,
          this.successPlaceholderTarget,
          this.submitTarget,
        )
        this.dispatch('uploadStateChange', { detail: { disableInput: false } })
        this.dispatch(
          'reloadFolder',
          { detail: { folderId: this.folderIdValue } },
        )
        break
      case 'waitingForFileUpload':
        this.submitTarget.innerHTML =
          this.submitTarget.dataset.waitingForFileUploadText
        this.submitTarget.disabled = true
        this.#show(
          this.albumTemplatePlaceholderTarget,
          this.fileListTarget,
          this.fileUploadContainerTarget,
          this.submitTarget,
        )
        this.dispatch('uploadStateChange', { detail: { disableInput: false } })
        break
      case 'waitingForTemplate':
      default:
        this.#show(this.fileUploadPlaceholderTarget)
    }
  }

  #addFileForm(originalFile) {
    if (this.hasFileTemplateTarget) {
      const file = new File(
        [originalFile],
        Valence.Utils.File.sanitizeFilename(originalFile.name),
        { type: originalFile.type },
      )
      const clone = this.fileTemplateTarget.content.cloneNode(true)
      const dataTransfer = new DataTransfer()
      dataTransfer.items.add(file)
      clone.querySelector('input[type=file]').files = dataTransfer.files
      this.fileListTarget.appendChild(clone)
    }
  }

  #checkFormsForAllSuccess() {
    const allSuccess = this.clientsPictureUploadFormOutlets.every(
      (form) => form.statusValue === 'success',
    )

    if (allSuccess) {
      this.stateValue = 'success'
    }
  }

  async #fetchAlbumForm(abortSignal) {
    let response

    try {
      response = await get(
        this.templateUrlValue,
        {
          query: {
            folder_id: this.folderIdValue,
            upload_type: 'album',
          },
          responseKind: 'json',
          signal: abortSignal,
        },
      )
    } catch (error) {
      if (error.name === 'AbortError') {
        return
      }

      throw error
    }

    if (!response.ok) {
      throw new Error(response.status)
    }

    const data = await response.json

    this.albumTemplatePlaceholderTarget.innerHTML = data.results
  }

  async #fetchPictureTemplate(abortSignal) {
    let response

    try {
      response = await get(
        this.templateUrlValue,
        {
          query: {
            folder_id: this.folderIdValue,
            upload_type: 'picture',
          },
          responseKind: 'json',
          signal: abortSignal,
        },
      )
    } catch (error) {
      if (error.name === 'AbortError') {
        return
      }

      throw error
    }

    if (!response.ok) {
      throw new Error(response.status)
    }

    const data = await response.json

    this.fileListTarget.innerHTML = data.results
  }

  async #fetchTemplates() {
    if (this.abortController) {
      this.abortController.abort('new template request')
    }

    this.abortController = new AbortController()

    await Promise.all(
      [
        this.#fetchPictureTemplate(this.abortController.signal),
        this.#fetchAlbumForm(this.abortController.signal),
      ],
    )

    this.stateValue = 'waitingForFileUpload'
  }

  #show(...elements) {
    [
      this.albumTemplatePlaceholderTarget,
      this.fileListTarget,
      this.fileUploadContainerTarget,
      this.fileUploadPlaceholderTarget,
      this.submitTarget,
      this.submittingPlaceholderTarget,
      this.successPlaceholderTarget,
    ].filter((el) => !elements.includes(el)).
      forEach((element) => leave(element))

    elements.forEach((element) => enter(element))
  }

  async #submitAlbumForm() {
    await this.clientsAlbumUploadFormOutlet.submit()

    if (this.clientsAlbumUploadFormOutlet.statusValue === 'success') {
      this.documentId = this.clientsAlbumUploadFormOutlet.documentIdValue
      this.stateValue = 'submittingPictures'
    } else {
      this.stateValue = 'active'
    }
  }

  async #submitPictures() {
    const promises = this.clientsPictureUploadFormOutlets.map(
      (form) => form.submit(this.documentId),
    )

    await Promise.allSettled(promises)

    this.#checkFormsForAllSuccess()
  }
}
